import { useState } from "react";
import { X, ChevronDown, ChevronUp, MoreHorizontal } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../Invoice/invoiceUtils";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function getExpiryDate(validityDays) {
  if (!validityDays) return "";
  const d = new Date();
  d.setDate(d.getDate() + Number(validityDays));
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function parseFeatures(str) {
  if (!str) return [];
  return str.split(/[.✔]/).map(s => s.replace(/^[\s✔]+/, "").trim()).filter(Boolean);
}

function parseCoverage(str) {
  if (!str) return [];
  return str.split(".").map(s => s.trim()).filter(Boolean);
}

// ─── CARD THEMES ─────────────────────────────────────────────────────────────
const CARD_THEMES = [
  {
    cardBg:    "linear-gradient(145deg, #F3F0FF 0%, #EDE8FF 30%, #F8F0FF 60%, #FFF0FB 100%)",
    bannerBg:  "linear-gradient(90deg, #A78BFA 0%, #818CF8 50%, #60A5FA 100%)",
    ctaBg:     "linear-gradient(135deg, #2DD4BF 0%, #14B8A6 100%)",
    ctaShadow: "0 8px 10px rgba(45,212,191,0.4)",
    checkColor:"#7C3AED",
    priceColor:"#1E1B4B",
    subtitleColor: "#6D28D9",
    claimsColor:   "#7C3AED",
  },
  {
    cardBg:    "linear-gradient(145deg, #EFF6FF 0%, #DBEAFE 30%, #E0F2FE 70%, #F0F9FF 100%)",
    bannerBg:  "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
    ctaBg:     "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
    ctaShadow: "0 8px 24px rgba(37,99,235,0.35)",
    checkColor:"#1D4ED8",
    priceColor:"#1E3A5F",
    subtitleColor: "#1D4ED8",
    claimsColor:   "#0369A1",
  },
  {
    cardBg:    "linear-gradient(145deg, #F0FFF4 0%, #DCFCE7 30%, #D1FAE5 70%, #F0FDFA 100%)",
    bannerBg:  "linear-gradient(90deg, #10B981 0%, #059669 50%, #0D9488 100%)",
    ctaBg:     "linear-gradient(135deg, #059669 0%, #10B981 100%)",
    ctaShadow: "0 8px 24px rgba(16,185,129,0.35)",
    checkColor:"#059669",
    priceColor:"#064E3B",
    subtitleColor: "#065F46",
    claimsColor:   "#047857",
  },
  {
    cardBg:    "linear-gradient(145deg, #FFFBEB 0%, #FEF3C7 30%, #FFF7ED 70%, #FFF0E6 100%)",
    bannerBg:  "linear-gradient(90deg, #F59E0B 0%, #EF4444 100%)",
    ctaBg:     "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)",
    ctaShadow: "0 8px 24px rgba(217,119,6,0.35)",
    checkColor:"#B45309",
    priceColor:"#451A03",
    subtitleColor: "#92400E",
    claimsColor:   "#B45309",
  },
];

const PRODUCT_THEME_MAP = {
  "Extended Warranty": CARD_THEMES[0],
  "Screen Damage":     CARD_THEMES[1],
  "Total Protection":  CARD_THEMES[2],
  "Device Insurance":  CARD_THEMES[3],
};

function getThemeByProductType(productType) {
  if (!productType) return CARD_THEMES[0];
  if (PRODUCT_THEME_MAP[productType]) return PRODUCT_THEME_MAP[productType];
  let hash = 0;
  for (let i = 0; i < productType.length; i++) {
    hash = productType.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CARD_THEMES[Math.abs(hash) % CARD_THEMES.length];
}

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: FeatureRow
// ─────────────────────────────────────────────────────────────────────────────
function FeatureRow({ text, color, muted }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-xs font-bold mt-0.5 shrink-0" style={{ color }}>✓</span>
      <span className="text-[13px] leading-snug" style={{ color: muted ? "#6B7280" : "#374151" }}>
        {text}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ATOM: PricePopup
// ─────────────────────────────────────────────────────────────────────────────
function PricePopup({ mrp, margin, payable, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: -8 }}
      animate={{ opacity: 1, scale: 1,   y: 0  }}
      exit={{   opacity: 0, scale: 0.9, y: -8  }}
      className="absolute top-10 right-0 z-50 w-44 rounded-2xl p-4 shadow-2xl"
      style={{ background: "white", border: "1px solid #E5E7EB" }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-black uppercase text-slate-600">Price Breakdown</span>
        <button onClick={onClose}><X size={12} className="text-slate-400"/></button>
      </div>
      {[
        { label: "MRP",     val: `₹${mrp}`,     color: "#111827" },
        { label: "Profit",  val: `₹${margin}`,  color: "#059669" },
        { label: "Payable", val: `₹${payable}`, color: "#D97706" },
      ].map(({ label, val, color }) => (
        <div key={label} className="flex justify-between items-center py-1.5 border-b border-slate-100 last:border-0">
          <span className="text-[11px] text-slate-500">{label}</span>
          <span className="text-[13px] font-bold" style={{ color }}>{val}</span>
        </div>
      ))}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOLECULE: WarrantyPlanCard
// ─────────────────────────────────────────────────────────────────────────────
export function WarrantyPlanCard({
  tpl,
  index = 0,
  isSelected,
  isMostPopular,
  onSelect,
  mopPrice = 0,
  expandAll,
  setExpandAll,
  isRetailerMobile,
}) {
  const [showPricePopup, setShowPricePopup] = useState(false);

  const product     = tpl?.warranty_product;
  const theme       = getThemeByProductType(product?.product_type);
  const isPercent   = Number(product?.is_percent) === 1;
  const effectiveMrp = isPercent
    ? Math.round((Number(product?.mrp || 0) / 100) * mopPrice)
    : Number(product?.mrp || 0);

  const mrp        = Number(product?.mrp || 0);
  const profit     = Number(tpl?.retailer_payout || 0);
  const payable    = mrp - profit;
  const isOffer    = product?.is_offer === 1;

  const features     = parseFeatures(product?.features);
  const coverageList = parseCoverage(product?.coverage);
  const visFeats     = expandAll ? features : features.slice(0, 5);

  const subscribedPackages = product?.subscribed_packages || [];
  const balanceLeft  = subscribedPackages[0]?.balance ?? 0;
  const isLowBalance = balanceLeft <= 5;
  const endDate      = subscribedPackages[0]?.end_date;
  const remainingDays = endDate
    ? Math.ceil((new Date(endDate) - new Date()) / 86400000)
    : null;

  const shortName = (product?.name || "Warranty Plan").split(" ").slice(0, 3).join(" ");

  const badges = [];
  if (isOffer)        badges.push("✓ Subscribed Plan");
  if (isMostPopular)  badges.push("Most Popular");

  const expiryTextColor =
    remainingDays !== null && remainingDays <= 3 ? "#EF4444" :
    remainingDays !== null && remainingDays <= 7 ? "#F59E0B" : null;

  // ── INNER CARD BG ──────────────────────────────────────────────────────────
  // Desktop: clean white  |  Mobile: beautiful themed gradient
  const innerCardBg = isRetailerMobile ? theme.cardBg : "#FFFFFF";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.97 }}
      animate={{ opacity: 1, y: 0,  scale: 1    }}
      transition={{ duration: 0.4, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
      onClick={onSelect}
      className="cursor-pointer"
      style={{ fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)" }}
    >
      {/* ── GRADIENT BORDER ── */}
      <div
        className="rounded-[28px]"
        style={{
          padding:    "3px",
          background: theme.bannerBg,
          boxShadow:  isSelected
            ? "0 20px 60px rgba(0,0,0,0.16)"
            : "0 4px 20px rgba(0,0,0,0.06)",
          transform:  isSelected ? "translateY(-6px) scale(1.02)" : "none",
          transition: "all 0.3s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        {/* Badge strip */}
        {badges.length > 0 && (
          <div
            className="flex flex-wrap gap-1 justify-center py-2 rounded-t-[26px] text-white text-[10px] font-black uppercase tracking-widest"
            style={{ background: "transparent" }}
          >
            {badges.map((b, i) => (
              <span key={i} className="px-2">{b}</span>
            ))}
          </div>
        )}

        {/* ── INNER CARD ── */}
        <div
          className="rounded-[26px] overflow-hidden"
          style={{ background: innerCardBg }}
        >
          <div className="px-4 pt-3 pb-5">

            {/* Header: image + name + ··· */}
            <div className="flex items-center justify-between mb-1 relative">
              <div className="flex items-center gap-2">
                {product?.image
                  ? <img src={product.image} alt="" className="w-7 h-7 rounded object-contain opacity-80"/>
                  : <span className="text-base opacity-60">🛡</span>
                }
                <h2
                  className="font-bold leading-tight"
                  style={{ fontSize: 16, color: "#1E1B4B" }}
                >
                  {shortName}
                </h2>
              </div>

              {/* ··· only for non-offer (shows price breakdown) */}
              {!isOffer && (
                <button
                  onClick={e => { e.stopPropagation(); setShowPricePopup(v => !v); }}
                  className="ml-2"
                >
                  <MoreHorizontal size={16} style={{ color: "#9CA3AF" }}/>
                </button>
              )}

              <AnimatePresence>
                {showPricePopup && !isOffer && (
                  <PricePopup
                    mrp={mrp} margin={profit} payable={payable}
                    onClose={() => setShowPricePopup(false)}
                  />
                )}
              </AnimatePresence>
            </div>

            {/* Subtitle row: expiry + enrolls badge */}
            <div className="flex items-center justify-between mb-3 ml-9">
              <p className="text-[11px]" style={{ color: expiryTextColor || theme.subtitleColor }}>
                {isOffer
                  ? remainingDays !== null
                    ? remainingDays > 0 ? `Expires in ${remainingDays} days` : "Expired"
                    : "Subscribed"
                  : `Valid until ${getExpiryDate(product?.validity)}`
                }
              </p>
              {isOffer && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: isLowBalance ? "#FEE2E2" : "#D1FAE5",
                    color:      isLowBalance ? "#DC2626"  : "#059669",
                  }}
                >
                  {balanceLeft} left
                </span>
              )}
            </div>

            {/* Price + validity row */}
            {!isOffer && (
              <div className="mb-4">
                <p className="font-black text-lg leading-none" style={{ color: theme.priceColor }}>
                  ₹{effectiveMrp.toLocaleString("en-IN")}
                </p>
                <p className="text-[11px] mt-0.5" style={{ color: theme.claimsColor }}>
                  {product?.validity} days validity · {product?.claims} claims
                </p>
              </div>
            )}

            {isOffer && (
              <p className="text-[11px] mb-4" style={{ color: theme.claimsColor }}>
                {product?.validity} days validity · {product?.claims} claims
              </p>
            )}

            {/* Feature checklist */}
            {!expandAll && (
              <div className="flex flex-col gap-2 mb-4">
                {visFeats.map((f, i) => (
                  <FeatureRow key={i} text={f} color={theme.checkColor} muted={isRetailerMobile}/>
                ))}
              </div>
            )}

            {/* CTA button */}
            <div className="flex justify-center mb-2">

              {/* ❌ SUBSCRIPTION BALANCE OVER */}
              {isOffer && balanceLeft <= 0 ? (

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                  }}
                  className="
                    px-5 py-2 rounded-full
                    text-[12px] font-bold
                    transition-all
                  "
                  style={{
                    background: "#FEE2E2",
                    color: "#DC2626",
                    border: "1px solid #FCA5A5",
                    minWidth: 220,
                  }}
                >
                  No Balance Left • Renew To Use
                </button>

              ) : (

                <button
                  onClick={e => {
                    e.stopPropagation();
                    onSelect();
                  }}
                  className="
                    px-8 py-2 rounded-full
                    text-white text-[13px]
                    font-bold transition-all
                    active:scale-95
                  "
                  style={{
                    background:
                      isSelected
                        ? "#10B981"
                        : theme.ctaBg,

                    boxShadow:
                      isSelected
                        ? "0 6px 20px rgba(16,185,129,0.4)"
                        : theme.ctaShadow,

                    minWidth: 120,
                  }}
                >
                  {isSelected
                    ? "✓ Selected"
                    : "Select Plan"}
                </button>

              )}
            </div>

            {/* Expanded: all features + coverage */}
            {expandAll && (
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-widest mb-2"
                    style={{ color: theme.checkColor }}>All Features</p>
                  <div className="flex flex-col gap-2">
                    {features.map((f, i) => (
                      <FeatureRow key={i} text={f} color={theme.checkColor} muted={isRetailerMobile}/>
                    ))}
                  </div>
                </div>
                {coverageList.length > 0 && (
                  <div>
                    <p className="text-[11px] font-black uppercase tracking-widest mb-2"
                      style={{ color: theme.checkColor }}>Coverage Includes</p>
                    <div className="flex flex-col gap-2">
                      {coverageList.map((c, i) => (
                        <FeatureRow key={i} text={c} color={theme.checkColor} muted={isRetailerMobile}/>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* View more / less toggle */}
            {(features.length > 5 || coverageList.length > 0) && (
              <div className="mt-3">
                <div className="relative flex items-center justify-center">
                  <div className="w-full border-t border-dashed" style={{ borderColor: `${theme.checkColor}40` }}/>
                  <button
                    onClick={e => { e.stopPropagation(); setExpandAll(v => !v); }}
                    className="absolute rounded-full p-1 shadow-sm border"
                    style={{
                      background:   innerCardBg,
                      borderColor:  `${theme.checkColor}30`,
                    }}
                  >
                    {expandAll
                      ? <ChevronUp  size={13} style={{ color: theme.checkColor }}/>
                      : <ChevronDown size={13} style={{ color: theme.checkColor }}/>
                    }
                  </button>
                </div>
                <p className="text-center mt-2 text-[11px] font-semibold"
                  style={{ color: theme.checkColor }}>
                  {expandAll ? "View Less" : "View More"}
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ORGANISM: WarrantyPlanGrid
// ─────────────────────────────────────────────────────────────────────────────
export function WarrantyPlanGrid({
  priceTemplates = [],
  selectedTemplate,
  setSelectedTemplate,
  updateField,
  form,
  expandAll,
  setExpandAll,
  expandedCoverage,
  setExpandedCoverage,
  isRetailerMobile,
}) {
  const navigate = useNavigate();

  const [showRenewModal, setShowRenewModal] = useState(false);
  if (!priceTemplates.length) return null;

  const mostPopularId = priceTemplates.reduce(
    (best, t) =>
      Number(t.warranty_product?.sold_count || 0) >
      Number(best.warranty_product?.sold_count || 0)
        ? t : best,
    priceTemplates[0]
  )?.id;

  return (
    <>
    
    <div className={cn(
      "mt-4",
      "flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-1",
      "sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:overflow-visible",
    )}>
      {priceTemplates.map((tpl, i) => (
        <div key={tpl.id} className="min-w-[85%] sm:min-w-0 snap-center">
          <WarrantyPlanCard
            tpl={tpl}
            index={i}
            mopPrice={Number(form?.mopPrice || 0)}
            isSelected={selectedTemplate?.id === tpl.id}
            isMostPopular={tpl.id === mostPopularId}
            expandAll={expandAll}
            setExpandAll={setExpandAll}
            expandedCoverage={expandedCoverage}
            setExpandedCoverage={setExpandedCoverage}
            isRetailerMobile={isRetailerMobile}
            onSelect={() => {

              const product =
                tpl?.warranty_product;

              const subscribedPackages =
                product?.subscribed_packages || [];

              const balanceLeft =
                subscribedPackages[0]?.balance ?? 0;

              const isSubscribedPlan =
                product?.is_offer === 1;

              // ✅ BLOCK IF BALANCE 0
              if (
                isSubscribedPlan &&
                balanceLeft <= 0
              ) {

                setShowRenewModal(
                  true
                );

                return;
              }

              // ✅ EXISTING LOGIC
              setSelectedTemplate(
                tpl
              );

              const exp =
                new Date();

              exp.setDate(
                exp.getDate() +
                  Number(
                    tpl.warranty_product
                      ?.validity || 0
                  )
              );

              updateField(
                "expiryDate",
                exp
                  .toISOString()
                  .split("T")[0]
              );
            }}
          />
        </div>
      ))}
    </div>

    {createPortal(
      <AnimatePresence>
        {showRenewModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="
              fixed inset-0
              z-[999999]
              bg-black/60
              backdrop-blur-sm
              flex items-center justify-center
            "
          >

            {/* MOBILE BOTTOM SHEET */}
            <motion.div
              initial={{
                y: isRetailerMobile ? 300 : 20,
                opacity: 0,
                scale: isRetailerMobile ? 1 : 0.95,
              }}
              animate={{
                y: 0,
                opacity: 1,
                scale: 1,
              }}
              exit={{
                y: isRetailerMobile ? 300 : 20,
                opacity: 0,
                scale: isRetailerMobile ? 1 : 0.95,
              }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 250,
              }}
              className={cn(
                "w-full bg-white shadow-2xl",

                // 📱 MOBILE STYLE
                isRetailerMobile
                  ? `
                    fixed bottom-0 left-0 right-0
                    rounded-t-[32px]
                    p-5
                    max-w-full

                    glass-small-card
                    bg-white/10
                    backdrop-blur-2xl
                    border border-white/20

                    shadow-[0_-10px_40px_rgba(0,0,0,0.35)]
                  `

                  // 💻 DESKTOP STYLE
                  : `
                    max-w-md
                    rounded-3xl
                    p-6
                  `
              )}
            >

              {/* MOBILE HANDLE */}
              {isRetailerMobile && (
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-1.5 rounded-full bg-slate-300" />
                </div>
              )}

              <div
                className={cn(
                  "text-center",

                  isRetailerMobile
                    ? "text-white"
                    : "text-slate-800"
                )}
              >

              {/* Handle */}
              {/* <div className="flex justify-center mb-4">
                <div className="w-14 h-1.5 rounded-full bg-white/40" />
              </div> */}

              {/* Icon */}
              <div className="text-5xl mb-3">
                ⚠️
              </div>

              {/* Title */}
              <h2
                className={cn(
                  "text-2xl font-bold mb-2",

                  isRetailerMobile
                    ? "text-white"
                    : "text-slate-800"
                )}
              >
                Insufficient Balance
              </h2>

              {/* Description */}
              <p
                className={cn(
                  "text-sm leading-relaxed mb-6",

                  isRetailerMobile
                    ? "text-white/70"
                    : "text-slate-600"
                )}
              >
                You don't have sufficient balance to enroll device.
                Please renew your subscription package.
              </p>

              {/* Buttons */}
              <div className="flex gap-3">

                <button
                  onClick={() => setShowRenewModal(false)}
                  className={cn("flex-1 py-3 rounded-2xl font-semibold",
                    isRetailerMobile
                      ? `
                        bg-white/10
                        border border-white/20
                        text-white
                        backdrop-blur-xl
                      `
                      : `
                        bg-slate-200
                        text-slate-700
                      `
                  )}
                >
                  Cancel
                </button>

                <button
                  onClick={() => navigate('/retproduct')}
                  className="
                    flex-1 py-3 rounded-2xl
                    bg-blue-500
                    text-white
                    font-semibold
                    shadow-lg shadow-blue-500/30
                  "
                >
                  Renew Now
                </button>

              </div>
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )}
    </>
  );
}

// import { useState } from "react";
// import { Check, Info, X, ChevronDown, ChevronUp, MoreHorizontal } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import { cn } from "../Invoice/invoiceUtils";

// // ─── HELPERS ──────────────────────────────────────────────────────────────────
// function getExpiryDate(validityDays) {
//   if (!validityDays) return "";
//   const d = new Date();
//   d.setDate(d.getDate() + Number(validityDays));
//   return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
// }


// function parseFeatures(str) {
//   if (!str) return [];
//   return str
//     .split(/[.✔]/)
//     .map(s => s.replace(/^[\s✔]+/, "").trim())
//     .filter(Boolean);
// }

// function parseCoverage(str) {
//   if (!str) return [];
//   return str
//     .split(".")
//     .map(s => s.trim())
//     .filter(Boolean);
// }

// function SplitTitle({ name, img, isOffer, onMoreClick }) {
//   const words = (name || "").split(" ");
//   const thin  = words.slice(0, -1).join(" ");

//   return (
//     <div className="flex justify-between items-center relative">
//       <div className="flex gap-2 items-center">
//         {img ? (
//           <img src={img} alt="" className="w-7 h-7 rounded object-contain opacity-70"/>
//         ) : (
//           <span className="text-base opacity-60">🛡</span>
//         )}

//         <h2 style={{ fontSize: 16, color: "#1E1B4B" }}>
//           <span style={{ fontWeight: 300 }}>{thin}</span>
//         </h2>
//       </div>

//       {/* ✅ SHOW ONLY IF NOT OFFER */}
//       {!isOffer && (
//         <button onClick={(e) => {
//           e.stopPropagation();
//           onMoreClick();
//         }}>
//           <MoreHorizontal size={14} style={{ color: "#9CA3AF" }} />
//         </button>
//       )}
//     </div>
//   );
// }

// function FeatureRow({ text, color }) {
//   return (
//     <div className="flex items-start gap-2.5">
//       <span
//         className="text-xs font-bold leading-tight mt-0.5 shrink-0"
//         style={{ color }}
//       >
//         ✓
//       </span>
//       <span className="text-[13px] leading-snug" style={{ color: "#374151" }}>
//         {text}
//       </span>
//     </div>
//   );
// }


// // ─────────────────────────────────────────────────────────────────────────────
// //  ATOM: PriceBreakdownPopup
// // ─────────────────────────────────────────────────────────────────────────────
// function PricePopup({ mrp, margin, payable, onClose }) {
//   return (
//     <motion.div
//       initial={{ opacity:0, scale:0.9, y:-8 }}
//       animate={{ opacity:1, scale:1,   y:0   }}
//       exit={{  opacity:0, scale:0.9, y:-8 }}
//       className="absolute top-10 right-0 z-50 w-35 rounded-2xl p-4 shadow-2xl"
//       style={{ background:"white", border:"1px solid rgba(255,255,255,0.12)" }}
//     >
//       <div className="flex items-center justify-between mb-3">
//         <span className="text-[10px] font-black uppercase text-slate-600">
//           Price Breakdown
//         </span>
//         <button onClick={onClose} className="text-slate-600 hover:text-white/80 transition">
//           <X size={12}/>
//         </button>
//       </div>
//       {[
//         { label:"MRP",     val:`₹${mrp}`,     color:"#000000"     },
//         { label:"Profit",  val:`₹${margin}`,  color:"#86EFAC"  },
//         { label:"Payable", val:`₹${payable}`, color:"#FCD34D"  },
//       ].map(({ label, val, color }) => (
//         <div key={label} className="flex justify-between items-center py-1.5 border-b border-white/10 last:border-0">
//           <span className="text-[11px] text-slate-600 font-body">{label}</span>
//           <span className="text-[13px] font-semibold font-display" style={{ color }}>{val}</span>
//         </div>
//       ))}
//     </motion.div>
//   );
// }


// const CARD_THEMES = [
//   {
//     // Purple-pink-lavender (exactly like reference)
//     cardBg:    "linear-gradient(145deg, #F3F0FF 0%, #EDE8FF 30%, #F8F0FF 60%, #FFF0FB 100%)",
//     bannerBg:  "linear-gradient(90deg, #A78BFA 0%, #818CF8 50%, #60A5FA 100%)",
//     ctaBg:     "linear-gradient(135deg, #2DD4BF 0%, #14B8A6 100%)",
//     ctaShadow: "0 8px 10px rgba(45,212,191,0.4)",
//     checkColor:"#7C3AED",
//     priceColor:"#1E1B4B",
//     border:    "linear-gradient(135deg, #C4B5FD, #A5B4FC, #FCA5A5)",
//   },
//   {
//     // Blue-cyan
//     cardBg:    "linear-gradient(145deg, #EFF6FF 0%, #DBEAFE 30%, #E0F2FE 70%, #F0F9FF 100%)",
//     bannerBg:  "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
//     ctaBg:     "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)",
//     ctaShadow: "0 8px 24px rgba(37,99,235,0.35)",
//     checkColor:"#1D4ED8",
//     priceColor:"#1E3A5F",
//     border:    "linear-gradient(135deg, #93C5FD, #67E8F9, #A5B4FC)",
//   },
//   {
//     // Green-mint
//     cardBg:    "linear-gradient(145deg, #F0FFF4 0%, #DCFCE7 30%, #D1FAE5 70%, #F0FDFA 100%)",
//     bannerBg:  "linear-gradient(90deg, #10B981 0%, #059669 50%, #0D9488 100%)",
//     ctaBg:     "linear-gradient(135deg, #059669 0%, #10B981 100%)",
//     ctaShadow: "0 8px 24px rgba(16,185,129,0.35)",
//     checkColor:"#059669",
//     priceColor:"#064E3B",
//     border:    "linear-gradient(135deg, #6EE7B7, #34D399, #67E8F9)",
//   },
//   {
//     // Peach-amber
//     cardBg:    "linear-gradient(145deg, #FFFBEB 0%, #FEF3C7 30%, #FFF7ED 70%, #FFF0E6 100%)",
//     bannerBg:  "linear-gradient(90deg, #F59E0B 0%, #EF4444 100%)",
//     ctaBg:     "linear-gradient(135deg, #D97706 0%, #F59E0B 100%)",
//     ctaShadow: "0 8px 24px rgba(217,119,6,0.35)",
//     checkColor:"#B45309",
//     priceColor:"#451A03",
//     border:    "linear-gradient(135deg, #FCD34D, #FCA5A5, #FDBA74)",
//   },
// ];


// const PRODUCT_THEME_MAP = {
//   "Extended Warranty": CARD_THEMES[0],   // purple
//   "Screen Damage": CARD_THEMES[1],   // blue
//   "Total Protection": CARD_THEMES[2],   // green
//   "Device Insurance": CARD_THEMES[3],    // orange
// };


// function getThemeByProductType(productType) {
//   if (!productType) return CARD_THEMES[0];

//   // direct match
//   if (PRODUCT_THEME_MAP[productType]) {
//     return PRODUCT_THEME_MAP[productType];
//   }

//   // fallback → hash-based stable color
//   let hash = 0;
//   for (let i = 0; i < productType.length; i++) {
//     hash = productType.charCodeAt(i) + ((hash << 5) - hash);
//   }

//   return CARD_THEMES[Math.abs(hash) % CARD_THEMES.length];
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: WarrantyPlanCard  — the star
// // ─────────────────────────────────────────────────────────────────────────────
// export function WarrantyPlanCard({
//   tpl,
//   index = 0,
//   isSelected,
//   isMostPopular,
//   onSelect,
//   mopPrice = 0,
//   expandAll,
//   setExpandAll,
//   isRetailerMobile,

// }) {

//   const [showPricePopup, setShowPricePopup] = useState(false);  
//   const productType = tpl?.warranty_product?.product_type;
//   const theme = getThemeByProductType(productType);
//   const product = tpl?.warranty_product;

//   const isPercent    = Number(product?.is_percent) === 1;
//   const effectiveMrp = isPercent
//     ? Math.round((Number(product?.mrp || 0) / 100) * mopPrice)
//     : Number(product?.mrp || 0);

//   const mrp = Number(product?.mrp || 0);
//   const profit = Number(tpl?.retailer_payout || 0);
//   const payable = mrp - profit;

//    const isSubscribed = tpl?.warranty_product?.is_offer === 1
//   const features      = parseFeatures(product?.features);
//   const featsExp      = expandAll;
//   const visFeats      = featsExp ? features : features.slice(0, 5);
//   const coverageList = parseCoverage(product?.coverage);
//   const subscribedPackages =
//   tpl?.warranty_product?.subscribed_packages || [];

//   const balanceLeft =
//     subscribedPackages.length > 0
//       ? subscribedPackages[0]?.balance
//       : 0;

//   const isLowBalance = balanceLeft <= 5;

//   const endDate = subscribedPackages[0]?.end_date;

//     let remainingDays = null;

//     if (endDate) {
//       const today = new Date();
//       const expiry = new Date(endDate);

//       const diffTime = expiry - today;
//       remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
//     }
  
//   // Banner text

//         // multiple badges support
//         const badges = [];

//         if (isSubscribed) {
//         badges.push("✓ Subscribed Plan");
//         }

//         if (isMostPopular) {
//         badges.push("Most Popular");
// }

//   // Short name — "Extended Warranty" style (first 2–3 words)
//   const shortName = (product?.name || "Warranty Plan")
//     .split(" ")
//     .slice(0, 3)
//     .join(" ");

//     const img = (product?.image)

//   const expiryColor =
//   remainingDays <= 3
//     ? "text-red-500"
//     : remainingDays <= 7
//     ? "text-yellow-500"
//     : "text-gray-400";

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 28, scale: 0.97 }}
//       animate={{ opacity: 1, y: 0,  scale: 1    }}
//       transition={{ duration: 0.45, delay: index * 0.07, ease: [0.16, 1, 0.3, 1] }}
//       onClick={onSelect}
//       className="cursor-pointer"
//       style={{ fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)" }}
//     >
//       {/* ── GRADIENT BORDER WRAPPER ──────────────────────────────────────── */}
//       <div
//         className="p-[3px] rounded-[28px]"
//         style={{
//           background:  theme.bannerBg,
//           boxShadow:   isSelected
//             ? `0 20px 60px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.6)`
//             : "0 4px 20px rgba(0,0,0,0.06)",
//           transform:   isSelected ? "translateY(-6px) scale(1.02)" : "none",
//           transition:  "all 0.3s cubic-bezier(0.16,1,0.3,1)",
//         }}
//       >

//         {badges.length > 0 && (
//         <div className="flex flex-wrap gap-1 justify-center py-2 rounded-t-[26px] text-white text-[10px] font-black uppercase tracking-[0.12em]">
//             {badges.map((b, i) => (
//             <span key={i} className="px-2 py-[2px] rounded-full">
//                 {b}
//             </span>
//             ))}
//         </div>
//         )}
//         {/* ── INNER CARD WITH GRADIENT BG ─────────────────────────────── */}
//        <div
//           className={cn(
//             "rounded-[26px] overflow-hidden",
//             isRetailerMobile
//               ? "bg-white/10 backdrop-blur-2xl border border-white/30"
//               : ""
//           )}
//           style={{
//   background: isRetailerMobile
//     ? "linear-gradient(145deg, rgba(255,255,255,0.35) 0%, rgba(219,234,254,0.25) 30%, rgba(191,219,254,0.20) 60%, rgba(147,197,253,0.15) 100%)"
//     : "white",
// }}
//         >

//           {/* ── TOP BANNER ── */}
          

//           {/* ── CARD BODY ── */}
//           <div className="px-3 pt-2 pb-6">

//             {/* Type label + ··· menu */}
//             <div className="flex items-center justify-between mb-3">
//               {/* <div className="flex items-center gap-2">
                
//                 <span
//                   className="text-[10px] font-semibold uppercase tracking-widest"
//                   style={{ color: "#9CA3AF" }}
//                 >
//                   {product?.product_type || "Warranty"}
//                 </span>
//               </div> */}
              
//             </div>

//             {/* ── BIG SPLIT TITLE — like "Micro dose" in reference ── */}
//             <SplitTitle name={shortName} img={img} isOffer={product?.is_offer} onMoreClick={() => setShowPricePopup((prev) => !prev)} />

//             {!product?.is_offer && showPricePopup && (
//                 <PricePopup
//                     mrp={mrp}
//                     margin={profit}
//                     payable={payable}
//                     onClose={() => setShowPricePopup(false)}
//                 />
//                 )}

//             <div className="flex justify-between">
//               {/* Subtitle */}
//             <p className="text-xs mt-1 mb-2 ml-9" style={{ color: "#9CA3AF" }}>
//               {isSubscribed ? (
//                 remainingDays > 0 ? (
//                   <span className={expiryColor}>
//                     Expires in {remainingDays} days
//                   </span>
//                 ) : (
//                   <span className="text-red-500 font-semibold">Expired</span>
//                 )
//               ) : (
//                 <>Valid until {getExpiryDate(product?.validity)}</>
//               )}
//             </p>

//             {/* ✅ SHOW ONLY FOR SUBSCRIBED PLAN */}
//             {isSubscribed && (
//               <div
//                 className={`px-2 py-1 rounded-full text-xs font-bold
//                   ${isLowBalance ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-700"}
//                 `}
//               >
//                 Enrolls Left: {balanceLeft}
//               </div>
//             )}
//             </div>

//             {/* ── CTA + PRICE ROW — exactly like reference ── */}
//             <div className="flex items-center justify-start mb-5">
              

//               {/* Price block — "₹1,499 /per plan" */}
//              <div className="text-left">
//                 {/* ❌ Hide MRP if offer */}
                
//                 {/* ✅ Final price */}
//                 {product?.is_offer !== 1 && (
//                 <p className="font-black text-md" style={{ color: theme.priceColor }}>
//                     ₹{effectiveMrp.toLocaleString("en-IN")}
//                 </p>
//                 )}

//                 <span className="text-sm" style={{ color: "#9CA3AF" }}>
//                     {product?.validity} validity / {product?.claims} claims
//                 </span>
//                 </div>
//             </div>

//             {/* ── FEATURE CHECKLIST ── */}
//             {!featsExp && (
//               <div className="flex flex-col gap-2">
//                   {visFeats.map((f, i) => (
//                   <FeatureRow key={i} text={f} color={theme.checkColor} />
//                   ))}
//               </div>
//               )}
                
//             <div className="flex justify-center p-2">

//                 <button
//                         onClick={e => { e.stopPropagation(); onSelect(); }}
//                         className="px-2 py-2 rounded-full text-white text-[12px] font-bold transition-all active:scale-95"
//                         style={{
//                         background:  isSelected ? "#10B981" : theme.ctaBg,
//                         boxShadow:   isSelected ? "0 8px 24px rgba(16,185,129,0.4)" : theme.ctaShadow,
//                         minWidth:    100,
//                         }}
//                     >
//                         {isSelected ? "✓ Selected" : "Select Plan"}
//                     </button>
//                 </div>

//                 {featsExp && (
//                 <div className="mt-5 space-y-4">

//                     {/* FEATURES */}
//                     <div>
//                     <p className="text-[12px] font-semibold text-gray-500 mb-2">
//                         All Features
//                     </p>

//                     <div className="flex flex-col gap-2">
//                         {features.map((f, i) => (
//                         <FeatureRow key={i} text={f} color={theme.checkColor} />
//                         ))}
//                     </div>
//                     </div>

//                     {/* COVERAGE */}
//                     {coverageList.length > 0 && (
//                     <div>
//                         <p className="text-[12px] font-semibold text-gray-500 mb-2">
//                         Coverage Includes
//                         </p>

//                         <div className="flex flex-col gap-2">
//                         {coverageList.map((c, i) => (
//                             <FeatureRow key={i} text={c} color={theme.checkColor} />
//                         ))}
//                         </div>
//                     </div>
//                     )}

//                 </div>
//                 )}

//                 {(features.length > 5 || coverageList.length > 0) && (
//                 <div className="mt-3">

//                     {/* DIVIDER WITH CENTER ARROW */}
//                     <div className="relative flex items-center justify-center">
//                     <div className="w-full border-t border-dashed border-gray-300"></div>

//                     <button
//                         onClick={(e) => {
//                         e.stopPropagation();
//                         setExpandAll((prev) => !prev);
//                         }}
//                         className="absolute bg-white border rounded-full p-1 shadow"
//                     >
//                         {featsExp ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
//                     </button>
//                     </div>

//                     {/* TEXT */}
//                     <div className="text-center mt-2 text-xs font-semibold text-gray-600">
//                     {featsExp ? "View Less" : "View More"}
//                     </div>

//                 </div>
//                 )}
//           </div>
        
//         </div>
//       </div>
//     </motion.div>
//   );
// }


// // ─────────────────────────────────────────────────────────────────────────────
// //  ORGANISM: WarrantyPlanGrid  — drop-in replacement for the existing card grid
// //  Usage: replace the `hidden md:grid` block in FormBody step 1
// // ─────────────────────────────────────────────────────────────────────────────
// export function WarrantyPlanGrid({
//   priceTemplates = [],
//   selectedTemplate,
//   setSelectedTemplate,
//   updateField,
//   form,
//   expandAll,
//   setExpandAll,
//   expandedCoverage,
//   setExpandedCoverage,
//   isRetailerMobile,
// }) {
//   if (!priceTemplates.length) return null;

//   const mostPopularId = priceTemplates.reduce((best, t) =>
//     Number(t.warranty_product?.sold_count||0) > Number(best.warranty_product?.sold_count||0) ? t : best,
//     priceTemplates[0]
//   )?.id;


//   return (
//     <div
//   className={cn(
//     "mt-4",
//     "flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-1",
//     "sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:overflow-visible"
//   )}
// >
//       {priceTemplates.map((tpl, i) => (
//         <div className="min-w-[85%] sm:min-w-0 snap-center">
//           <WarrantyPlanCard
//             key={tpl.id}
//             tpl={tpl}
//             index={i}
//             mopPrice={Number(form?.mopPrice || 0)}
//             isSelected={selectedTemplate?.id === tpl.id}
//             isMostPopular={tpl.id === mostPopularId}
//             expandAll={expandAll}
//             setExpandAll={setExpandAll}
//             expandedCoverage={expandedCoverage}
//             setExpandedCoverage={setExpandedCoverage}
//             onSelect={() => {
//               setSelectedTemplate(tpl);

//               const exp = new Date();
//               exp.setDate(exp.getDate() + Number(tpl.warranty_product?.validity || 0));

//               updateField("expiryDate", exp.toISOString().split("T")[0]);
//             }}
//             isRetailerMobile={isRetailerMobile}
//           />
//         </div>
//       ))}
//     </div>
//   );
// }